import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { DatePipe } from '@angular/common';
import { NgSelectModule } from '@ng-select/ng-select';
import { AppRoutingModule } from './app-routing.module';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { AgmCoreModule } from '@agm/core';
import { MatSortModule } from '@angular/material/sort';
import { MatPaginatorModule } from '@angular/material/paginator';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { HTTP_INTERCEPTORS, HttpClientModule, HttpClientXsrfModule } from '@angular/common/http';
import { SharedModule } from './shared/shared.module';
import { TranslateFileLoader } from './TranslateFileLoader';
import { LoaderService } from './shared/loader/loader.service';
import { HeaderService } from './shared/header/header.service';
import { LoaderInterceptorService } from './shared/loader/loader-interceptor.service';
import { NotificationMessageService } from './shared/notification-message/notification-message.service';
import { AppComponent } from './app.component';
import { MainLayoutComponent } from './layouts/main-layout.component';
import { SimpleLayoutComponent } from './layouts/simple-layout.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { UnauthorizedComponent } from './unauthorized/unauthorized.component';
import { LoginComponent } from './users/login/login.component';
import { ForgotPasswordComponent } from './users/forgot-password/forgot-password.component';
import { CaptchaComponent } from './users/captcha/captcha.component';
import { UsersComponent } from './users/users.component';
import { EditUserComponent } from './users/edit-user/edit-user.component';
import { RegisterComponent } from './users/register/register.component';
import { ConfirmUserEmailComponent } from './users/confirm-user-email/confirm-user-email.component';
import { RegistrationRequestsComponent } from './admin/registration-requests/registration-requests.component';
import { ValidateRegistrationRequestsComponent } from './admin/validate-registration-requests/validate-registration-requests.component';
import { CustomersComponent } from './customers/customers.component';
import { CustomerEditPageComponent } from './customers/customer-edit-page/customer-edit-page.component';
import { HomeComponent } from './home/home.component';
import { EnterpriseComponent } from './enterprise/enterprise.component';
import { EnterpriseEditComponent } from './enterprise/enterprise-edit/enterprise-edit.component';
import { SitesComponent } from './sites/sites.component';
import { SiteEditPageComponent } from './sites/site-edit-page/site-edit-page.component';
import { CommonDataService } from './shared/services/common-data.service';
import { ValidationService } from './shared/services/validation.service';
import { NavigationService } from './shared/services/navigation.service';
import { CustomerInfoComponent } from './home/customer-dashboard/customer-info/customer-info.component';
import { SearchDevicesComponent } from './device/search-devices/search-devices.component';
import { ManageDevicePageComponent } from './device/manage-device-page/manage-device-page.component';
import { ReverseEventsComponent } from './device/reverse-events/reverse-events.component';
import { ReverseCalibrationComponent } from './device/reverse-calibration/reverse-calibration.component';
import { ReverseRtComponent } from './device/reverse-rt/reverse-rt.component';
import { ErrorLogComponent } from './admin/logs/error-log/error-log.component';
import { ViewErrorComponent } from './admin/logs/error-log/view-error/view-error.component';
import { SchedControlComponent } from './admin/sched/sched-control/sched-control.component';
import { SchedMonitorComponent } from './admin/sched/sched-monitor/sched-monitor.component';
import { AdminService } from './admin/admin.service';
import { SiteDashboardComponent } from './home/site-dashboard/site-dashboard.component';
import { SideMenuService } from './shared/side-menu/side-menu.service';
import { LeftPanelService } from './shared/left-panel/left-panel.service';
import { SiteInfoComponent } from './home/site-dashboard/site-info/site-info.component';
import { SiteMailerSubscriptionsComponent } from './home/site-dashboard/site-info/site-mailer-subscriptions/site-mailer-subscriptions.component';
import { FirmwareUpdateComponent } from './management/firmware-update/firmware-update.component';
import { DeviceListingAdminComponent } from './admin/device/device-listing-admin/device-listing-admin.component';
import { DeviceEditPageComponent } from './admin/device/device-edit-page/device-edit-page.component';
import { simUsageManagementComponent } from './admin/sim-usage-management/sim-usage-management.component';
import { EditAccessFunctionsComponent } from './users/edit-access-functions/edit-access-functions.component';
import { RtComponent } from './home/site-dashboard/device/device-dashboard/rt/rt.component';
import { EventsComponent } from './home/site-dashboard/device/device-dashboard/events/events.component';
import { ConnectivityTrackComponent } from './home/site-dashboard/device/device-dashboard/connectivity-track/connectivity-track.component';
import { ConfigTrackComponent } from './home/site-dashboard/device/device-dashboard/config-track/config-track.component';
import { QuickViewComponent } from './home/site-dashboard/device/device-dashboard/quick-view/quick-view.component';
import { DeviceSettingsComponent } from './home/site-dashboard/device/device-dashboard/device-settings/device-settings.component';
import { SetAdcComponent } from './home/site-dashboard/device/device-dashboard/set-adc/set-adc.component';
import { GlobalRecordsComponent } from './home/site-dashboard/device/device-dashboard/global-records/global-records.component';
import { DebugRecordsComponent } from './home/site-dashboard/device/device-dashboard/debug-records/debug-records.component';
import { DeviceManagementService } from './device/device-management.service';
import { DeviceService } from './home/site-dashboard/device/device.service';
import { BreadcrumbService } from './shared/breadcrumb/breadcrumb.service';
import { PermissionEditComponent } from './admin/permission-edit/permission-edit.component';
import { DevicesListingComponent } from './home/site-dashboard/device/devices-listing/devices-listing.component';
import { DeviceDashboardComponent } from './home/site-dashboard/device/device-dashboard/device-dashboard.component';
import { DeviceSelectCriteriaComponent } from './home/site-dashboard/device/device-select-criteria/device-select-criteria.component';
import { SitesMapComponent } from './home/sites-map/sites-map.component';
import { DeviceActionsComponent } from './home/site-dashboard/device/device-dashboard/device-actions/device-actions.component';
import { AdminComponent } from './admin/admin.component';
import { ZombieSitesComponent } from './admin/customer/zombie-sites/zombie-sites.component';
import { RejectedDeviceConnectionComponent } from './admin/logs/rejected-device-connection/rejected-device-connection.component';
import { ActionLogComponent } from './admin/logs/action-log/action-log.component';
import { BecomeUserComponent } from './users/become-user/become-user.component';
import { DailyDetailsComponent } from './home/site-dashboard/device/device-dashboard/daily-details/daily-details.component';
import { EditUserPasswordComponent } from './users/edit-user-password/edit-user-password.component';
import { ContactUsComponent } from './users/contact-us/contact-us.component';
import { ContactUsAdminComponent } from './admin/contact-us-admin/contact-us-admin.component';
import { ViewContactUsAdminComponent } from './admin/view-contact-us-admin/view-contact-us-admin.component';
import { BlockedFwReportsComponent } from './admin/blocked-fw-reports/blocked-fw-reports.component';
import { DevicePerformanceAnalyticsComponent } from './home/site-dashboard/device/device-dashboard/device-performance-analytics/device-performance-analytics.component';
import { GoogleChartsModule } from 'angular-google-charts';
import { NgxPrintModule } from 'ngx-print';
import { UsageDataComponent } from './home/usage-data/usage-data.component';
import { QuarterlyUsageDataComponent } from './home/quarterly-usage-data/quarterly-usage-data.component';
import { LoginNonConfirmedEmailComponent } from './users/login-non-confirmed-email/login-non-confirmed-email.component';
import { FontAwesomeModule, FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
import { FooterService } from './shared/footer/footer.service';
import { CookiePolicyComponent } from './home/cookie-policy/cookie-policy.component';
import { AccountDeletionComponent } from './home/account-deletion/account-deletion.component';
import { GoogleMapService } from './shared/google-map/google-map.service';
import { SitePerformanceComponent } from './home/site-dashboard/site-performance/site-performance.component';
import { ChartDataService } from './shared/chart-data-widget/chart-data.service';
import { PermissionsComponent } from './permissions/permissions.component';
import { PermissionsEditComponent } from './permissions/permissions-edit/permissions-edit.component';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { NoteService } from './shared/note/note.service';
import { SiteNotesComponent } from './home/site-dashboard/site-notes/site-notes.component';
import { DeviceNotesComponent } from './home/site-dashboard/device/device-dashboard/device-notes/device-notes.component';
import { TagsManagementComponent } from './home/site-dashboard/tags-management/tags-management.component';
import { ReplaceDeviceComponent } from './device/replace-device/replace-device.component';
import { ManageDeviceRequestComponent } from './device/manage-device-request/manage-device-request.component';
import { ManagementRequestsComponent } from './admin/device/management-requests/management-requests.component';
import { RevealPasswordRequestsComponent } from './device/reveal-password-requests/reveal-password-requests.component';
import { DeviceRequestsComponent } from './device/management-requests/device-requests/device-requests.component';
import { CustomerRequestsComponent } from './device/management-requests/customer-requests/customer-requests.component';
import { SiteRequestsComponent } from './device/management-requests/site-requests/site-requests.component';
import { CustomerRequestEditComponent } from './device/management-requests/customer-request-edit/customer-request-edit.component';
import { SiteRequestEditComponent } from './device/management-requests/site-request-edit/site-request-edit.component';
import { DeviceReplacementRequestsComponent } from './device/management-requests/device-replacement-requests/device-replacement-requests.component';
import { SiteWarningsComponent } from './home/site-dashboard/site-warnings/site-warnings.component';
import { WarningsComponent } from './admin/warnings/warnings.component';
import { ForceReadDebugComponent } from './device/force-read-debug/force-read-debug.component';
import { DeviceAdminComponent } from './home/site-dashboard/device/device-dashboard/device-admin/device-admin.component';
import { TermsAndConditionsComponent } from './home/terms-and-conditions/terms-and-conditions.component';
import { MaintenanceReportComponent } from './home/site-dashboard/maintenance-report/maintenance-report.component';
import { ReceivingShipmentComponent } from './management/receiving-shipment/receiving-shipment.component';
import { ShipmentQualityComponent } from './management/shipment-quality/shipment-quality.component';
import { ShippingOutComponent } from './management/shipping-out/shipping-out.component';
import { SiteWarrantyComponent } from './site-dashboard/site-warranty/site-warranty.component';
import { SiteSettingsPage } from './home/site-dashboard/site-settings/site-settings-page/site-settings-page.component';
import { TimeLostReportComponent } from './admin/time-lost-report/time-lost-report.component';
import { DownloadStatusComponent } from './home/site-dashboard/download-status/download-status.component';
import { ActionsHistoryComponent } from './home/site-dashboard/device/device-dashboard/actions-history/actions-history.component';
import { warningSettingsComponent } from './home/site-dashboard/device/device-dashboard/warning-settings/warning-settings.component';
import { DeviceReportComponent } from './admin/device-report/device-report.component';
import { AdminActionsComponent } from './admin/sched/admin-actions/admin-actions.component';
import { GoogleAnalytics } from './shared/services/google-analytics.service';
import { AuditComponent } from './admin/audit/audit.component';
import { UserAuditComponent } from './admin/audit/user-audit/user-audit.component';
import { PermissionAuditComponent } from './admin/audit/permission-audit/permission-audit.component';
import { UpdateDeviceFwComponent } from './home/site-dashboard/device/device-dashboard/update-device-fw/update-device-fw.component';
import { MatButtonModule } from '@angular/material/button';
import { LandingPageComponent } from './users/landing-page/landing-page.component';
import { SchedRunComponent } from './admin/sched/sched-run/sched-run.component';
import { UploadFwFilesComponent } from './admin/upload-fw-files/upload-fw-files.component';
import { UnblockLoginComponent } from './users/unblock-login/unblock-login.component';
import { CellularSettingsComponent } from './home/site-dashboard/device/device-dashboard/cellular-settings/cellular-settings.component';
import { CellularTrackComponent } from './home/site-dashboard/device/device-dashboard/cellular-track/cellular-track.component';
import { OfficialReportComponent } from './home/site-dashboard/device/device-dashboard/official-report/official-report.component';
import { IccidChangesReportComponent } from './iccid-changes-report/iccid-changes-report.component';
import { ShipmentHistoryComponent } from './management/shipment-history/shipment-history.component';
import { DeviceAttachmentsComponent } from './home/site-dashboard/device/device-dashboard/device-attachments/device-attachments.component';
import { SiteAttachmentsComponent } from './home/site-dashboard/site-attachments/site-attachments.component';
import { SimManagementComponent } from './admin/sim-management/sim-management.component';
import { MultiTariffService } from './admin/multi-tariff/multi-tariff.service';
import { ProfilesListComponent } from './admin/multi-tariff/profiles-list/profiles-list.component';
import { EditProfileComponent } from './admin/multi-tariff/edit-profile/edit-profile.component';
import { RatesComponent } from './admin/multi-tariff/rates/rates.component';
import { CategoriesComponent } from './admin/multi-tariff/categories/categories.component';
import { SmartRebatesSitesComponent } from './admin/smart-rebates-sites/smart-rebates-sites.component';
import { DisconnectedDevicesComponent } from './admin/disconnected-device/disconnected-device.component';
import { DisconnectedSitesComponent } from './admin/disconnected-sites-report/disconnected-sites-report.component';
import { ManageOrdersComponent } from './management/orders/manage-orders/manage-orders.component';
import { EditOrderComponent } from './management/orders/edit-order/edit-order.component';
import { RmaComponent } from './management/rma/rma.component';
import { RmaProcessingComponent } from './management/rma-processing/rma-processing.component';
import { ReceivingShipmentHistoryComponent } from './management/receiving-shipment-history/receiving-shipment-history.component';
import { ReceivingShipmentHistoryResultComponent } from './management/receiving-shipment-history-result/receiving-shipment-history-result.component';
import { RegistrationReportsComponent } from './registration-reports/registration-reports.component';
import { ApisLogsComponent } from './admin/apis-logs/apis-logs.component';
import { UnusedLogsComponent } from './admin/logs/unused-logs/unused-logs.component';
import { EditDevicesOwnersComponent } from './admin/device/edit-devices-owners/edit-devices-owners.component';
import { ConnectivityVerificationLogComponent } from './admin/logs/connectivity-verification-log/connectivity-verification-log.component';
import { FakeDataGeneratorComponent } from './admin/fake-data-generator/fake-data-generator.component';
import { UpdateFirmwareLogsComponent } from './admin/logs/update-firmware-logs/update-firmware-logs.component';
import { ConnectivityFrequencyComponent } from './home/site-dashboard/device/device-dashboard/connectivity-frequency/connectivity-frequency.component';
import { StudiesService } from './study/studies.service';
import { SiteStudiesComponent } from './home/site-dashboard/site-studies/site-studies.component';
import { MonitoringDevicesPageComponent } from './admin/monitoring-devices-page/monitoring-devices-page.component';
import { ReceivingShipmentService } from './management/receiving-shipment/receiving-shipment.service';
import { DeviceQueueChangesComponent } from './management/device-queue-changes/device-queue-changes.component';
import { ConnectivityVerificationStatisticsComponent } from './admin/logs/connectivity-verification-statistics/connectivity-verification-statistics.component';
import { DeviceLocationMismatchReportComponent } from './admin/device-location-mismatch-report/device-location-mismatch-report.component';
import { BulkFillDeviceInfoComponent } from './home/site-dashboard/device/bulk_fill_device_info/bulk_fill_device_info.component';
import { IncorrectEventsComponent } from './admin/incorrect-events/incorrect-events.component';
import { DisableExternalRtcComponent } from './management/disable-external-rtc/disable-external-rtc.component';
import { SuggestedInstallationDateReportComponent } from './noc-tools/suggested-installation-date-report/suggested-installation-date-report.component';
import { DisableXTRAFileComponent } from './management/disable-xtra-file/disable-xtra-file.component';
import { MissingSimsComponent } from './admin/missing-sims/missing-sims.component';
import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { UsageTimeWidgetComponent } from './home/usage-time-widget/usage-time-widget.component';
import { SearchDeviceEventsReportComponent } from './noc-tools/search-device-events-report/search-device-events-report.component';
import { RtRequestsHistoryComponent } from './management/rt-requests-history/rt-requests-history.component';
import { RtRequestHistoryService } from './management/rt-requests-history/rt-request-history.service';
import { DefectiveDevicesPageComponent } from './noc-tools/defective-devices-page/defective-devices-page.component';
import { NocConfigurationsComponent } from './noc-tools/noc-configurations/noc-configurations.component';
import { NocConfigurationEditComponent } from './noc-tools/noc-configurations/noc-configuration-edit/noc-configuration-edit.component';
import { NocConfigurationRecipientsComponent } from './noc-tools/noc-configurations/noc-configuration-recipients/noc-configuration-recipients.component';
import { MultiForceReadDebugComponent } from './noc-tools/multi-force-read-debug/multi-force-read-debug.component';
import { EbuAndChargeOpportunityComponent } from './home/ebu-and-charge-opportunity/ebu-and-charge-opportunity.component';
import { DebugLogsComponent } from './admin/debug-logs/debug-logs.component';
import { QuickViewHistoryComponent } from './noc-tools/quick-view-history/quick-view-history.component';
import { DevicesWithoutLocationComponent } from './management/devices-without-location/devices-without-location.component';
import { SitesGroupComponent } from './admin/sites-group/sites-group.component';
import { DemoSitePairsComponent } from './admin/demo-site-pairs/demo-site-pairs.component';
import { ChargerPredictionComponent } from './home/site-dashboard/device/device-dashboard/charger-prediction/charger-prediction.component';
import { ConfigureStudyDevicesComponent } from './management/configure-study-devices/configure-study-devices.component';
import { SCTTableModule } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/sct-table.module';
import { SiteSettingsComponent } from './home/site-dashboard/site-settings/site-settings/site-settings.component';
import { BatteryPerformanceComponent } from './home/site-dashboard/device/device-dashboard/battery-performance/battery-performance.component';
import { PowerStudySocChartComponent } from './home/site-dashboard/device/device-dashboard/power-study-soc-chart/power-study-soc-chart.component';
import { StudyChargeCycleChartComponent } from './home/site-dashboard/device/device-dashboard/study-charge-cycle-chart/study-charge-cycle-chart.component';
import { VpcChartsComponent } from './home/site-dashboard/device/device-dashboard/vpc-charts/vpc-charts.component';
import { AppVersionSettingComponent } from './admin/app-version-setting/app-version-setting.component';
import { BatteriesCustomizedChartsComponent } from './home/site-dashboard/device/device-dashboard/batteries-customized-charts/batteries-customized-charts.component';
import { DeviceAlertsComponent } from './home/site-dashboard/device/device-dashboard/device-alerts/device-alerts.component';
import { BattlinkSitePerformanceComponent } from './home/site-dashboard/battlink-site-performance/battlink-site-performance.component';
import { SitePerformanceWidgetComponent } from './home/site-dashboard/battlink-site-performance/site-performance-widget/site-performance-widget.component';
import { BatteryFleetUtilizationComponent } from './home/site-dashboard/battlink-site-performance/battery-fleet-utilization/battery-fleet-utilization.component';
import { EbuDailyUsageSummaryComponent } from './home/site-dashboard/battlink-site-performance/ebu-daily-usage-summary/ebu-daily-usage-summary.component';
import { EqWeeklySummaryComponent } from './home/site-dashboard/battlink-site-performance/eq-weekly-summary/eq-weekly-summary.component';
import { IdleAfterEventTimeComponent } from './home/site-dashboard/battlink-site-performance/idle-after-event-time/idle-after-event-time.component';
import { HourlyIdleTimeComponent } from './home/site-dashboard/battlink-site-performance/hourly-idle-time/hourly-idle-time.component';
import { BatteryAlertSummaryComponent } from './home/site-dashboard/battlink-site-performance/battery-alert-summary/battery-alert-summary.component';
import { SiteEbusComponent } from './home/site-dashboard/battlink-site-performance/site-ebus/site-ebus.component';
import { SiteBatteryAgingComponent } from './home/site-dashboard/battlink-site-performance/site-battery-aging/site-battery-aging.component';
import { BatteryRemainingWarrantyComponent } from './home/site-dashboard/battlink-site-performance/battery-remaining-warranty/battery-remaining-warranty.component';
import { IotahUtilizationReportComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/iotah-utilization-report/iotah-utilization-report.component';
import { CharglinkUtilizationReportComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/charglink-utilization-report/charglink-utilization-report.component';
import { BattlinkUtilizationReportComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/battlink-utilization-report/battlink-utilization-report.component';
import { SiteFleetUtilizationScoreComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/iotah-utilization-report/site-fleet-utilization-score/site-fleet-utilization-score.component';
import { SiteEnergyUsageScoreComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/iotah-utilization-report/site-energy-usage-score/site-energy-usage-score.component';
import { TruckUtilizationScoreComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/iotah-utilization-report/truck-utilization-score/truck-utilization-score.component';
import { DailyFleetUtilizationComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/iotah-utilization-report/daily-fleet-utilization/daily-fleet-utilization.component';
import { HourlyFleetUtilizationComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/iotah-utilization-report/hourly-fleet-utilization/hourly-fleet-utilization.component';
import { DailyAhFleetUtilizationComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/iotah-utilization-report/daily-ah-fleet-utilization/daily-ah-fleet-utilization.component';
import { RelativeTruckUtilizationComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/iotah-utilization-report/relative-truck-utilization/relative-truck-utilization.component';
import { ChargerFleetUtilizationScoreComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/charglink-utilization-report/charger-fleet-utilization-score/charger-fleet-utilization-score.component';
import { ChargerUtilizationScoreComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/charglink-utilization-report/charger-utilization-score/charger-utilization-score.component';
import { DailyChargerUtilizationComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/charglink-utilization-report/daily-charger-utilization/daily-charger-utilization.component';
import { BatteryAhReturnScoreComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/battlink-utilization-report/battery-ah-return-score/battery-ah-return-score.component';
import { DailyBatteryFleetUtilizationComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/battlink-utilization-report/daily-battery-fleet-utilization/daily-battery-fleet-utilization.component';
import { DailyAhBatteryFleetUtilizationComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/battlink-utilization-report/daily-ah-battery-fleet-utilization/daily-ah-battery-fleet-utilization.component';
import { PercentBatteryUtilizationScoresComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/battlink-utilization-report/percent-battery-utilization-scores/percent-battery-utilization-scores.component';
import { ScoreChartComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/widgets/score-chart/score-chart.component';
import { DailyUtilizationChartComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/widgets/daily-utilization-chart/daily-utilization-chart.component';
import { HorizontalChartComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/widgets/horizantal-chart/horizantal-chart.component';
import { CharglinkBrmPublicComponent } from './site-dashboard/charglink-brm-public/charglink-brm-public.component';
import { CharglinkBrmSiteHistoryComponent } from './site-dashboard/charglink-brm-site-history/charglink-brm-site-history.component';
import { CharglinkBrmComponent } from './site-dashboard/charglink-brm/charglink-brm.component';
import { BrmGroupsComponent } from './site-dashboard/brm-groups/brm-groups.component';
import { CustomerUtilizationChartComponent } from './home/customer-dashboard/customer-utilization-chart/customer-utilization-chart.component';
import { EnergyUtilizationChartsComponent } from './home/customer-dashboard/energy-utilization-charts/energy-utilization-charts.component';
import { BattlinkEnergyUtilizationChartsComponent } from './home/customer-dashboard/battlink-energy-utilization-charts/battlink-energy-utilization-charts.component';
import { TruckUsageDataComponent } from './home/customer-dashboard/truck-usage-data/truck-usage-data.component';
import { PerSiteChartsService } from './shared/services/per-site-charts.service';
import { DomainsService } from './shared/services/domains.service';
import { BeforeLoginLayoutComponent } from './layouts/before-login-layout/before-login-layout.component';
import { ContactUsFormComponent } from './users/contact-us-form/contact-us-form.component';
import { ResetPasswordFormComponent } from './users/reset-pasword-form/reset-pasword-form.component';
import { ChargersUtilizationSettingsComponent } from './home/site-dashboard/site-settings/site-utilization-settings/chargers-utilization-settings/chargers-utilization-settings.component';
import { TruckUtilizationSettingsComponent } from './home/site-dashboard/site-settings/site-utilization-settings/truck-utilization-settings/truck-utilization-settings.component';
import { BatteryUtilizationSettingsComponent } from './home/site-dashboard/site-settings/site-utilization-settings/battery-utilization-settings/battery-utilization-settings.component';
import { ChargingFleetReportsComponent } from './home/site-dashboard/fleet-report/charging-fleet-reports/charging-fleet-reports.component';
import { DailyChargerUsageReportComponent } from './home/site-dashboard/fleet-report/daily-charger-usage-report/daily-charger-usage-report.component';
import { MonthlyQuarterlyChargerUsageReportComponent } from './home/site-dashboard/fleet-report/monthly-quarterly-charger-usage-report/monthly-quarterly-charger-usage-report.component';
import { DailyHourlyChargerUsageDataComponent } from './home/site-dashboard/fleet-report/daily-hourly-charger-usage-data/daily-hourly-charger-usage-data.component';
import { MonthlyQuarterlyChargerUsageChartsComponent } from './home/site-dashboard/fleet-report/monthly-quarterly-charger-usage-charts/monthly-quarterly-charger-usage-charts.component';
import { ChargerUsageDataComponent } from './home/site-dashboard/chargers-performace/charger-usage-data/charger-usage-data.component';
import { ChargerMonthlyQuarterlyUsageReportComponent } from './home/site-dashboard/chargers-performace/charger-monthly-quarterly-usage-report/charger-monthly-quarterly-usage-report.component';
import { DailyTruckUsageReportComponent } from './home/site-dashboard/truck-performance/daily-truck-usage-report/daily-truck-usage-report.component';
import { MonthlyQuarterlyTruckUsageReportComponent } from './home/site-dashboard/truck-performance/monthly-quarterly-truck-usage-report/monthly-quarterly-truck-usage-report.component';
import { MonthlyDailyTruckUsageChartComponent } from './home/site-dashboard/truck-performance/monthly-daily-truck-usage-chart/monthly-daily-truck-usage-chart.component';
import { DetailedRankingPageComponent } from './site-dashboard/detailed-ranking-page/detailed-ranking-page.component';
import { BatteryExceptionReportsComponent } from './home/site-dashboard/device/device-dashboard/battery-exception-reports/battery-exception-reports.component';
import { BatteryExceptionWidgetComponent } from './home/site-dashboard/device/device-dashboard/battery-exception-reports/battery-exception-widget/battery-exception-widget.component';
import { SiteTruckReportsComponent } from './home/site-dashboard/site-truck-reports/site-truck-reports.component';
import { SiteBatteriesReportsComponent } from './home/site-dashboard/device/device-dashboard/batteries-reports/site-batteries-reports/site-batteries-reports.component';
import { DailyBatteryUsageReportComponent } from './home/site-dashboard/device/device-dashboard/batteries-reports/daily-battery-usage-report/daily-battery-usage-report.component';
import { MonthlyQuarterlyBatteryUsageReportComponent } from './home/site-dashboard/device/device-dashboard/batteries-reports/monthly-quarterly-battery-usage-report/monthly-quarterly-battery-usage-report.component';
import { DailyMonthlyQuarterlyBatteryUsageChartsComponent } from './home/site-dashboard/device/device-dashboard/batteries-reports/daily-monthly-quarterly-battery-usage-charts/daily-monthly-quarterly-battery-usage-charts.component';
import { EndVoltageChartComponent } from './home/site-dashboard/device/device-dashboard/batteries-reports/end-voltage-chart/end-voltage-chart.component';
import { UsagePercentChartComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/widgets/usage-percent-chart/usage-percent-chart.component';
import { PowerUsageChartsComponent } from './home/site-dashboard/device/device-dashboard/power-usage-charts/power-usage-charts.component';
import { BrmSiteSettingsComponent } from './home/site-dashboard/site-settings/brm-site-settings/brm-site-settings.component';
import { BrmDevicesSimulationComponent } from './admin/brm-devices-simulation/brm-devices-simulation.component';
import { BrmPotentiallyMalfunctionedChargersComponent } from './admin/brm-potentially-malfunctioned-chargers/brm-potentially-malfunctioned-chargers.component';
import { ReplacementDevicesHistoryComponent } from './noc-tools/replacement-devices-history/replacement-devices-history.component';
import { ImDevicesGlobalRecordsComponent } from './home/site-dashboard/device/meter-device/im-device-global-records/im-device-global-records.component';
import { GlitchesComponent } from './home/site-dashboard/device/device-dashboard/glitches/glitches.component';
import { LoadSchedulingComponent } from './home/site-dashboard/device/device-dashboard/load-scheduling/load-scheduling.component';
import { IMUsageDataComponent } from './home/site-dashboard/device/device-dashboard/im-performance/usage-data/im-usage-data.component';
import { ImPeakPowerComponent } from './home/site-dashboard/device/device-dashboard/im-performance/im-peak-power/im-peak-power.component';
import { ImTotalEnergyUsageComponent } from './home/site-dashboard/device/device-dashboard/im-total-energy-usage/im-total-energy-usage.component';
import { DevicesConnectivityComponent } from './devices-connectivity/devices-connectivity.component';
import { PerformanceScoreWidgetComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/widgets/performance-score-widget/performance-score-widget.component';
import { PrivacyComponent } from './hame/privacy/privacy.component';
import { AppsComponent } from './hame/apps/apps.component';
import { BatteryLifeMetricsComponent } from './home/site-dashboard/device/device-dashboard/battery-life-metrics/battery-life-metrics.component';
import { ImUsageReportComponent } from './home/im-usage-report/im-usage-report.component';
import { DistributionChartComponent } from './home/site-dashboard/device/device-dashboard/batteries-reports/distribution-chart/distribution-chart.component';
import { UtilizationMetricBoxComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/widgets/utilization-metric-box/utilization-metric-box.component';
import { UtilizationMetricBoxLegendsComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/widgets/utilization-metric-box-legends/utilization-metric-box-legends.component';
import { DailyBattlinkUsageMetricsComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/daily-battlink-usage-metrics/daily-battlink-usage-metrics.component';
import { EnergySiteSettingsComponent } from './home/site-dashboard/site-settings/site-utilization-settings/energy-site-settings/energy-site-settings.component';
import { SiteSummaryBatteryReportComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/battlink-utilization-report/site-summary-battery-report/site-summary-battery-report.component';
import { SummaryHeaderComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/battlink-utilization-report/site-summary-battery-report/summary-header/summary-header.component';
import { AvgDailyUsageComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/battlink-utilization-report/site-summary-battery-report/avg-daily-usage/avg-daily-usage.component';
import { BatteryPerformanceMetricsComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/battlink-utilization-report/site-summary-battery-report/battery-performance-metrics/battery-performance-metrics.component';
import { BatteryReportComponent } from './home/site-dashboard/device/device-dashboard/battery-report/battery-report.component';
import { IotemeterSiteEnergyUsageComponent } from './home/site-dashboard/iotemeter/iotemeter-site-energy-usage/iotemeter-site-energy-usage.component';
import { IotemeterSiteDailyReportsComponent } from './home/site-dashboard/iotemeter/iotemeter-site-daily-reports/iotemeter-site-daily-reports.component';
import { SitePanelsComponent } from './home/site-dashboard/iotemeter/site-panels/site-panels.component';
import { SiteEnergyConsumptionComponent } from './home/site-dashboard/iotemeter/site-energy-consumption/site-energy-consumption.component';
import { MeterEnergyReportComponent } from './home/site-dashboard/iotemeter/meter-energy-report/meter-energy-report.component';
import { SiteEnergyReportComponent } from './home/site-dashboard/iotemeter/site-energy-report/site-energy-report.component';
import { EnergyConsumptionChartsComponent } from './home/site-dashboard/iotemeter/energy-consumption-charts/energy-consumption-charts.component';
import { IotemeterSiteExceptionReportsComponent } from './home/site-dashboard/iotemeter/iotemeter-site-exception-reports/iotemeter-site-exception-reports.component';
import { TruckSiteSummaryReportComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/iotah-utilization-report/truck-site-summary-report/truck-site-summary-report.component';
import { TruckSummaryReportHeaderComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/iotah-utilization-report/truck-summary-report-header/truck-summary-report-header.component';
import { TruckSummaryReportPerformanceComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/iotah-utilization-report/truck-summary-report-performance/truck-summary-report-performance.component';
import { TruckFleetUsageSummaryComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/iotah-utilization-report/truck-fleet-usage-summary/truck-fleet-usage-summary.component';
import { TruckUtilizationChartsComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/iotah-utilization-report/truck-utilization-score/truck-utilization-charts/truck-utilization-charts.component';
import { TruckReportComponent } from './home/site-dashboard/device/device-dashboard/truck-report/truck-report.component';
import { TruckReportHeaderComponent } from './home/site-dashboard/device/device-dashboard/truck-report/truck-report-header/truck-report-header.component';
import { TruckPerformanceMetricsComponent } from './home/site-dashboard/device/device-dashboard/truck-report/truck-performance-metrics/truck-performance-metrics.component';
import { DailyTruckFleetActivityMetricsComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/iotah-utilization-report/daily-truck-fleet-activity-metrics/daily-truck-fleet-activity-metrics.component';
import { ExceptionReportWidgetComponent } from './home/site-dashboard/device/device-dashboard/exception-report-widget/exception-report-widget.component';
import { ChargerExceptionReportsComponent } from './home/site-dashboard/device/device-dashboard/charger-exception-reports/charger-exception-reports.component';
import { TruckExceptionReportsComponent } from './home/site-dashboard/device/device-dashboard/truck-exception-reports/truck-exception-reports.component';
import { TruckSummaryReportPerformanceFiltersComponent } from './home/site-dashboard/device/device-dashboard/utilization-reports/iotah-utilization-report/truck-summary-report-performance-filters/truck-summary-report-performance-filters.component';
import { PowerStudyMetricsComponent } from './home/site-dashboard/device/device-dashboard/power-study-metrics/power-study-metrics.component';
import { DailyUsageMetricsComponent } from './home/site-dashboard/device/device-dashboard/power-study-metrics/daily-usage-metrics/daily-usage-metrics.component';
import { DailyUsageChartsComponent } from './home/site-dashboard/device/device-dashboard/power-study-metrics/daily-usage-charts/daily-usage-charts.component';
import { PowerStudyHeaderReportComponent } from './home/site-dashboard/device/device-dashboard/power-study-metrics/power-study-header-report/power-study-header-report.component';
import { DeviceEndVoltageChartsComponent } from './home/site-dashboard/device/device-dashboard/device-end-voltage-charts/device-end-voltage-charts.component';
import { StudiesDashboardComponent } from './study/studies-dashboard/studies-dashboard.component';
import { StudiesInventoryComponent } from './study/studies-inventory/studies-inventory.component';
import { StudiesSectionComponent } from './study/studies-active-completed/studies-active-completed.component';
import { StudyFiltersComponent } from './study/study-filters/study-filters.component';
import { PowerStudyReportsComponent } from './home/site-dashboard/device/device-dashboard/power-study-reports/power-study-reports.component';

// AoT requires an exported function for factories
export function TranslateFileLoaderFactory() {
	return new TranslateFileLoader(new DomainsService);
}
@NgModule({
	declarations: [
		AppComponent,
		MainLayoutComponent,
		SimpleLayoutComponent,
		PageNotFoundComponent,
		UnauthorizedComponent,
		CustomersComponent,
		LoginComponent,
		ForgotPasswordComponent,
		CaptchaComponent,
		UsersComponent,
		EditUserComponent,
		RegisterComponent,
		ConfirmUserEmailComponent,
		RegistrationRequestsComponent,
		ValidateRegistrationRequestsComponent,
		CustomerEditPageComponent,
		HomeComponent,
		EnterpriseComponent,
		EnterpriseEditComponent,
		SitesComponent,
		SiteEditPageComponent,
		CustomerInfoComponent,
		SearchDevicesComponent,
		ManageDevicePageComponent,
		ReverseEventsComponent,
		ReverseCalibrationComponent,
		ReverseRtComponent,
		EventsComponent,
		QuickViewComponent,
		RtComponent,
		ConnectivityTrackComponent,
		ErrorLogComponent,
		ViewErrorComponent,
		SchedControlComponent,
		SchedMonitorComponent,
		SiteDashboardComponent,
		EditAccessFunctionsComponent,
		SiteInfoComponent,
		SiteMailerSubscriptionsComponent,
		DeviceActionsComponent,
		ConfigTrackComponent,
		GlobalRecordsComponent,
		SiteInfoComponent,
		SetAdcComponent,
		FirmwareUpdateComponent,
		DeviceSettingsComponent,
		DebugRecordsComponent,
		DeviceListingAdminComponent,
		DeviceEditPageComponent,
		simUsageManagementComponent,
		PermissionEditComponent,
		DevicesListingComponent,
		BulkFillDeviceInfoComponent,
		DeviceDashboardComponent,
		DeviceSelectCriteriaComponent,
		SitesMapComponent,
		AdminComponent,
		ZombieSitesComponent,
		RejectedDeviceConnectionComponent,
		ActionLogComponent,
		BecomeUserComponent,
		DailyDetailsComponent,
		EditUserPasswordComponent,
		ContactUsComponent,
		ContactUsAdminComponent,
		ViewContactUsAdminComponent,
		BlockedFwReportsComponent,
		DevicePerformanceAnalyticsComponent,
		LoginNonConfirmedEmailComponent,
		UsageDataComponent,
		QuarterlyUsageDataComponent,
		// DateRangeSelectComponent,
		CookiePolicyComponent,
		AccountDeletionComponent,
		SitePerformanceComponent,
		PermissionsComponent,
		PermissionsEditComponent,
		SiteNotesComponent,
		DeviceNotesComponent,
		TagsManagementComponent,
		ReplaceDeviceComponent,
		ManageDeviceRequestComponent,
		ManagementRequestsComponent,
		RevealPasswordRequestsComponent,
		DeviceRequestsComponent,
		CustomerRequestsComponent,
		SiteRequestsComponent,
		CustomerRequestEditComponent,
		SiteRequestEditComponent,
		DeviceReplacementRequestsComponent,
		SiteWarningsComponent,
		WarningsComponent,
		DisconnectedDevicesComponent,
		DisconnectedSitesComponent,
		ForceReadDebugComponent,
		DeviceAdminComponent,
		TermsAndConditionsComponent,
		MaintenanceReportComponent,
		ReceivingShipmentComponent,
		ShipmentQualityComponent,
		ShippingOutComponent,
		SiteWarrantyComponent,
		SiteSettingsPage,
		TimeLostReportComponent,
		DownloadStatusComponent,
		ActionsHistoryComponent,
		warningSettingsComponent,
		DeviceReportComponent,
		UserAuditComponent,
		AuditComponent,
		PermissionAuditComponent,
		AdminActionsComponent,
		UpdateDeviceFwComponent,
		LandingPageComponent,
		SchedRunComponent,
		UploadFwFilesComponent,
		UnblockLoginComponent,
		CellularSettingsComponent,
		CellularTrackComponent,
		OfficialReportComponent,
		SmartRebatesSitesComponent,
		IccidChangesReportComponent,
		ShipmentHistoryComponent,
		IncorrectEventsComponent,
		DeviceAttachmentsComponent,
		SiteAttachmentsComponent,
		SimManagementComponent,
		ProfilesListComponent,
		EditProfileComponent,
		RatesComponent,
		CategoriesComponent,
		ManageOrdersComponent,
		EditOrderComponent,
		RmaComponent,
		RmaProcessingComponent,
		RegistrationReportsComponent,
		ReceivingShipmentHistoryComponent,
		ReceivingShipmentHistoryResultComponent,
		RegistrationReportsComponent,
		ApisLogsComponent,
		UnusedLogsComponent,
		EditDevicesOwnersComponent,
		ConnectivityVerificationLogComponent,
		FakeDataGeneratorComponent,
		UpdateFirmwareLogsComponent,
		SiteStudiesComponent,
		DeviceQueueChangesComponent,
 		ConnectivityVerificationStatisticsComponent,
		ConnectivityFrequencyComponent,
		DeviceLocationMismatchReportComponent,
		MonitoringDevicesPageComponent,
		DisableExternalRtcComponent,
		SuggestedInstallationDateReportComponent,
		DisableXTRAFileComponent,
		MissingSimsComponent,
		UsageTimeWidgetComponent,
		SearchDeviceEventsReportComponent,
		RtRequestsHistoryComponent,
		DefectiveDevicesPageComponent,
		NocConfigurationsComponent,
		NocConfigurationEditComponent,
		NocConfigurationRecipientsComponent,
		MultiForceReadDebugComponent,
		EbuAndChargeOpportunityComponent,
		DebugLogsComponent,
		QuickViewHistoryComponent,
		DevicesWithoutLocationComponent,
		SitesGroupComponent,
		DemoSitePairsComponent,
		ChargerPredictionComponent,
		ConfigureStudyDevicesComponent,
		SiteSettingsComponent,
		AppVersionSettingComponent,
		BatteriesCustomizedChartsComponent,
		BatteryPerformanceComponent,
		StudyChargeCycleChartComponent,
		PowerStudySocChartComponent,
		VpcChartsComponent,
		DeviceAlertsComponent,
		BattlinkSitePerformanceComponent,
		SitePerformanceWidgetComponent,
		BatteryFleetUtilizationComponent,
		EbuDailyUsageSummaryComponent,
		EqWeeklySummaryComponent,
		IdleAfterEventTimeComponent,
		HourlyIdleTimeComponent,
		BatteryAlertSummaryComponent,
		SiteEbusComponent,
		SiteBatteryAgingComponent,
		BatteryRemainingWarrantyComponent,
		IotahUtilizationReportComponent,
		CharglinkUtilizationReportComponent,
		BattlinkUtilizationReportComponent,
		SiteFleetUtilizationScoreComponent,
		SiteEnergyUsageScoreComponent,
		TruckUtilizationScoreComponent,
		DailyFleetUtilizationComponent,
		HourlyFleetUtilizationComponent,
		DailyAhFleetUtilizationComponent,
		RelativeTruckUtilizationComponent,
		ChargerFleetUtilizationScoreComponent,
		ChargerUtilizationScoreComponent,
		DailyChargerUtilizationComponent,
		BatteryAhReturnScoreComponent,
		DailyBatteryFleetUtilizationComponent,
		DailyAhBatteryFleetUtilizationComponent,
		PercentBatteryUtilizationScoresComponent,
		ScoreChartComponent,
		DailyUtilizationChartComponent,
		HorizontalChartComponent,
		CharglinkBrmPublicComponent,
		CharglinkBrmSiteHistoryComponent,
		CharglinkBrmComponent,
		BrmGroupsComponent,
		CustomerUtilizationChartComponent,
		EnergyUtilizationChartsComponent,
		BattlinkEnergyUtilizationChartsComponent,
		TruckUsageDataComponent,
		BeforeLoginLayoutComponent,
		ContactUsFormComponent,
		ResetPasswordFormComponent,
		ChargersUtilizationSettingsComponent,
		TruckUtilizationSettingsComponent,
		BatteryUtilizationSettingsComponent,
		ChargingFleetReportsComponent,
		DailyChargerUsageReportComponent,
		MonthlyQuarterlyChargerUsageReportComponent,
		DailyHourlyChargerUsageDataComponent,
		MonthlyQuarterlyChargerUsageChartsComponent,
		ChargerUsageDataComponent,
		ChargerMonthlyQuarterlyUsageReportComponent,
		DailyTruckUsageReportComponent,
		MonthlyQuarterlyTruckUsageReportComponent,
		MonthlyDailyTruckUsageChartComponent,
		DetailedRankingPageComponent,
		BatteryExceptionReportsComponent,
		BatteryExceptionWidgetComponent,
		ExceptionReportWidgetComponent,
		ChargerExceptionReportsComponent,
		TruckExceptionReportsComponent,
		SiteTruckReportsComponent,
		SiteBatteriesReportsComponent,
		DailyBatteryUsageReportComponent,
		MonthlyQuarterlyBatteryUsageReportComponent,
		DailyMonthlyQuarterlyBatteryUsageChartsComponent,
		EndVoltageChartComponent,
		UsagePercentChartComponent,
		PowerUsageChartsComponent,
		BrmSiteSettingsComponent,
		BrmDevicesSimulationComponent,
		BrmPotentiallyMalfunctionedChargersComponent,
		ReplacementDevicesHistoryComponent,
		ImDevicesGlobalRecordsComponent,
		GlitchesComponent,
		LoadSchedulingComponent,
		IMUsageDataComponent,
		ImPeakPowerComponent,
		ImTotalEnergyUsageComponent,
		DevicesConnectivityComponent,
		PerformanceScoreWidgetComponent,
		PrivacyComponent,
		AppsComponent,
		BatteryLifeMetricsComponent,
		ImUsageReportComponent,
		DistributionChartComponent,
		UtilizationMetricBoxComponent,
		UtilizationMetricBoxLegendsComponent,
		DailyBattlinkUsageMetricsComponent,
		EnergySiteSettingsComponent,
		SiteSummaryBatteryReportComponent,
		SummaryHeaderComponent,
		AvgDailyUsageComponent,
		BatteryPerformanceMetricsComponent,
		BatteryReportComponent,
		IotemeterSiteEnergyUsageComponent,
		IotemeterSiteDailyReportsComponent,
		SitePanelsComponent,
		SiteEnergyConsumptionComponent,
		MeterEnergyReportComponent,
		SiteEnergyReportComponent,
		EnergyConsumptionChartsComponent,
		IotemeterSiteExceptionReportsComponent,
		TruckSiteSummaryReportComponent,
		TruckSummaryReportHeaderComponent,
		TruckSummaryReportPerformanceComponent,
		TruckFleetUsageSummaryComponent,
		TruckUtilizationChartsComponent,
		TruckReportComponent,
		TruckReportHeaderComponent,
		TruckPerformanceMetricsComponent,
		DailyTruckFleetActivityMetricsComponent,
		TruckSummaryReportPerformanceFiltersComponent,
		PowerStudyMetricsComponent,
		DailyUsageMetricsComponent,
		DailyUsageChartsComponent,
		PowerStudyHeaderReportComponent,
		DeviceEndVoltageChartsComponent,
		StudiesDashboardComponent,
		StudiesInventoryComponent,
		StudiesSectionComponent,
		StudyFiltersComponent,
		PowerStudyReportsComponent,
	],
	imports: [
		AppRoutingModule,
		FormsModule,
		ReactiveFormsModule,
		SharedModule,
		HttpClientModule,
		HttpClientXsrfModule.withOptions({
			cookieName: 'XSRF-TOKEN',
			headerName: 'X-XSRF-Token',
		}),
		BrowserAnimationsModule,
		BsDropdownModule.forRoot(),
		TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader,
				useFactory: TranslateFileLoaderFactory,
			}
		}),
		NgSelectModule,
		MatMenuModule,
		MatIconModule,
		MatCheckboxModule,
		MatSortModule,
		MatSlideToggleModule,
		BsDatepickerModule.forRoot(),
		AgmCoreModule.forRoot({
				apiKey: 'AIzaSyCFxLjFwH5pExZgmEqWTovW8kWjWD4d3HE',
				libraries: ["places"]
		}),
		GoogleChartsModule,
		NgxPrintModule,
		FontAwesomeModule,
		TooltipModule.forRoot(),
		MatButtonModule,
		MatPaginatorModule,
		NgxSliderModule,
		SCTTableModule
	],
	providers: [
		TranslateService,
		LoaderService,
		HeaderService,
		DatePipe,
		{
			provide: HTTP_INTERCEPTORS,
			useClass: LoaderInterceptorService,
			multi: true
		},
		NotificationMessageService,
		CommonDataService,
		PerSiteChartsService,
		DomainsService,
		ValidationService,
		NavigationService,
		AdminService,
		MultiTariffService,
		SideMenuService,
		StudiesService,
		LeftPanelService,
		DeviceManagementService,
		DeviceService,
		FooterService,
		BreadcrumbService,
		GoogleMapService,
		ChartDataService,
		NoteService,
		GoogleAnalytics,
		ReceivingShipmentService,
		RtRequestHistoryService
	],
	bootstrap: [AppComponent]
})
export class AppModule {
	constructor(library: FaIconLibrary) {
		// Add an icon to the library for convenient access in other components
		library.addIconPacks(fas, far, fab);
	}
}
